/** Sentinel returned by list APIs when large embedded media is omitted. */

export const MEDIA_OMITTED = '__MEDIA_OMITTED__';

export function isMediaOmitted(value) {
  return value === MEDIA_OMITTED;
}

const QUESTION_MEDIA_FIELDS = ['sampleAudioUrl', 'audioUrl', 'videoUrl', 'imageUrl', 'url'];
const ELEMENT_MEDIA_FIELDS = ['videoUrl', 'audioUrl', 'docUrl'];

export function hasOmittedMediaInExerciseQuestions(questions) {
  if (!Array.isArray(questions)) return false;
  for (const q of questions) {
    if (!q || typeof q !== 'object') continue;
    for (const field of QUESTION_MEDIA_FIELDS) {
      if (isMediaOmitted(q[field])) return true;
    }
    if (Array.isArray(q.options)) {
      for (const opt of q.options) {
        if (opt && typeof opt === 'object' && isMediaOmitted(opt.url)) return true;
      }
    }
  }
  return false;
}

export function hasOmittedMediaInResponseAnswers(response) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return false;
  for (const [key, val] of Object.entries(response)) {
    if (key.startsWith('__')) continue;
    if (isMediaOmitted(val)) return true;
    if (val && typeof val === 'object') {
      if (hasOmittedMediaInResponseAnswers(val)) return true;
    }
  }
  return false;
}

export function responseNeedsFullFetch(response) {
  if (!response) return false;
  return !!response.mediaTruncated || hasOmittedMediaInResponseAnswers(response.response);
}

export function exerciseNeedsFullFetch(exercise) {
  if (!exercise) return false;
  if (exercise.mediaTruncated || exercise.questionsOmitted) return true;
  if (!Array.isArray(exercise.questions)) return true;
  return hasOmittedMediaInExerciseQuestions(exercise.questions);
}

export function elementNeedsFullFetch(element) {
  if (!element) return false;
  if (element.mediaTruncated || element.contentTruncated) return true;
  if (element.category === 'readable-content' && !String(element.content || '').trim()) {
    return true;
  }
  for (const field of ELEMENT_MEDIA_FIELDS) {
    if (isMediaOmitted(element[field])) return true;
  }
  return false;
}

export function questionBankItemNeedsFullFetch(item) {
  if (!item) return false;
  if (item.mediaTruncated || item.questionOmitted || !item.question) return true;
  return hasOmittedMediaInExerciseQuestions([item.question]);
}

/** Merge one question without downgrading loaded media to omitted placeholders. */
export function mergeQuestionMedia(prevQ, nextQ) {
  if (!nextQ) return prevQ;
  if (!prevQ) return nextQ;
  const out = { ...nextQ };
  for (const field of QUESTION_MEDIA_FIELDS) {
    const prevVal = prevQ[field];
    const nextVal = nextQ[field];
    if (isMediaOmitted(nextVal) && prevVal && !isMediaOmitted(prevVal)) {
      out[field] = prevVal;
    }
  }
  if (Array.isArray(nextQ.options)) {
    out.options = nextQ.options.map((opt, i) => {
      const prevOpt = Array.isArray(prevQ.options) ? prevQ.options[i] : null;
      if (!opt || typeof opt !== 'object') return opt;
      const prevUrl = prevOpt?.url;
      const nextUrl = opt.url;
      if (isMediaOmitted(nextUrl) && prevUrl && !isMediaOmitted(prevUrl)) {
        return { ...opt, url: prevUrl };
      }
      return opt;
    });
  }
  return out;
}

/** Keep loaded media when parent re-renders with slimmed list rows (omitted URLs). */
export function mergeExerciseQuestionsMedia(prevQuestions, nextQuestions) {
  if (!Array.isArray(nextQuestions)) return prevQuestions;
  if (!Array.isArray(prevQuestions)) return nextQuestions;
  return nextQuestions.map((nextQ, i) => {
    const prevQ = prevQuestions[i];
    if (!prevQ || String(prevQ.id) !== String(nextQ?.id)) {
      const byId = prevQuestions.find((pq) => pq && String(pq.id) === String(nextQ?.id));
      return mergeQuestionMedia(byId, nextQ);
    }
    return mergeQuestionMedia(prevQ, nextQ);
  });
}
